import { ORGANIZATION_ROLES_LABELS } from "@common/constants";
import {
    organizationUserRolesInOrder,
    OrganizationUserRole,
} from "@common/userOrganizationRoles";
import { useActiveOrganizationRole } from "@hooks/permissions/organization/useActiveOrganizationRole";
import { ReactNode, useMemo } from "react";

const assignableOrganizationRoles = [
    OrganizationUserRole.MEMBER,
    OrganizationUserRole.MANAGER,
    OrganizationUserRole.ADMIN,
] as const satisfies OrganizationUserRole[];

type AssignableOrganizationRole = (typeof assignableOrganizationRoles)[number];

const organizationRoleDescription: Record<
    AssignableOrganizationRole,
    ReactNode
> = {
    [OrganizationUserRole.MEMBER]:
        "Has access to assigned Clients and Entities.",
    [OrganizationUserRole.MANAGER]: (
        <>
            Has access to and can edit assigned Clients and Entities.
            <br />
            Can create new Clients and manage access for other Managers and
            Members.
        </>
    ),
    [OrganizationUserRole.ADMIN]: (
        <>
            Has full access to all Clients, Entities, and the Firm workspace.
            <br />
            Can manage access for all users (Admins, Managers, and Members) and
            view Billing.
        </>
    ),
};

const organizationRoleOptions = assignableOrganizationRoles
    .map((role) => ({
        value: role,
        label: ORGANIZATION_ROLES_LABELS[role],
        description: organizationRoleDescription[role],
    }))
    .toSorted(
        (a, b) =>
            organizationUserRolesInOrder.indexOf(a.value) -
            organizationUserRolesInOrder.indexOf(b.value),
    );

interface OrganizationRoleOption {
    value: OrganizationUserRole;
    label: string;
    description: ReactNode;
}

export function useOrganizationRoleOptions(): OrganizationRoleOption[] {
    const actingUserRole = useActiveOrganizationRole();

    const selectableOrganizationRoles = useMemo(() => {
        if (!actingUserRole) {
            return [];
        }

        return organizationUserRolesInOrder.slice(
            organizationUserRolesInOrder.indexOf(actingUserRole),
        );
    }, [actingUserRole]);

    return useMemo(
        () =>
            organizationRoleOptions.filter((role) =>
                selectableOrganizationRoles.includes(role.value),
            ),
        [selectableOrganizationRoles],
    );
}
